import React, { useCallback, useState, memo, useRef } from 'react';
import { Button, Input } from 'reactstrap';
import { LuFileJson2 } from 'react-icons/lu';
import { BsUiChecksGrid } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { setStructureData, addColumn, addRow } from '@/redux/slices/payoutStructure';
import VirtualizedPayoutTable, { VirtualizedPayoutTableRef } from './VirtualizedPayoutTable';
import styles from '../payout-structures.module.css';
import { FaArrowDownShortWide } from 'react-icons/fa6';
import { RiInsertColumnRight } from 'react-icons/ri';

const PayoutStructureForm = memo(() => {
  const dispatch = useDispatch();
  const virtualizedTableRef = useRef<VirtualizedPayoutTableRef>(null);

  const { name, playersColumns } = useSelector((state: any) => state.payoutStructure);

  const [inputJson, setInputJson] = useState<boolean>(false);
  const [jsonData, setJsonData] = useState('');

  const handleInputJsonChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      try {
        setJsonData(e.target.value);

        const inputValue = e.target.value;
        if (!inputValue) {
          return;
        }

        const parsedJson = JSON.parse(inputValue);

        const formattedColumns = Object.keys(parsedJson).reduce((acc, curr) => {
          if (curr.includes('-')) {
            const [playersFrom, playersTo] = curr.split('-');
            acc.push({
              playersFrom: Number(playersFrom),
              playersTo: Number(playersTo),
              percentages: parsedJson[curr],
            });
            return acc;
          }

          acc.push({
            playersFrom: Number(curr),
            playersTo: Number(curr),
            percentages: parsedJson[curr],
          });
          return acc;
        }, [] as any[]);

        const longestArray = formattedColumns.reduce((acc, curr) => {
          return Math.max(acc, curr.percentages.length);
        }, 0);

        const formattedRows = Array.from({ length: longestArray }, (_, index) => {
          return {
            rankFrom: index + 1,
            rankTo: index + 1,
          };
        });

        dispatch(
          setStructureData({
            name,
            playersColumns: formattedColumns,
            rankRows: formattedRows,
          }),
        );
      } catch (error) {
        console.error('Error parsing JSON:', error);
      }
    },
    [dispatch, name],
  );

  const formatJsonData = useCallback(() => {
    const baseData = {};
    for (const column of playersColumns) {
      if (column.playersFrom === column.playersTo) {
        baseData[column.playersFrom] = column.percentages.filter(Boolean);
      } else {
        baseData[`${column.playersFrom}-${column.playersTo}`] = column.percentages.filter(Boolean);
      }
    }
    return JSON.stringify(baseData, null, 2);
  }, [playersColumns]);

  const handleToggleInputJson = useCallback(() => {
    if (inputJson) {
      setInputJson(false);
    } else {
      setInputJson(true);
      setJsonData(formatJsonData());
    }
  }, [inputJson, formatJsonData]);

  const handleAddColumn = useCallback(() => {
    dispatch(addColumn());

    // Auto-scroll to the right after adding a column
    setTimeout(() => {
      virtualizedTableRef.current?.scrollToRight();
    }, 100);
  }, [dispatch]);

  const handleAddRow = useCallback(() => {
    dispatch(addRow());

    // Auto-scroll to the bottom after adding a row
    setTimeout(() => {
      virtualizedTableRef.current?.scrollToBottom();
    }, 100);
  }, [dispatch]);

  return (
    <>
      <div className="d-flex justify-content-start align-items-center my-2 gap-2">
        <Button
          className="d-flex justify-content-center align-items-center gap-2"
          color="primary"
          onClick={handleToggleInputJson}
          type="button"
        >
          {inputJson ? (
            <>
              <BsUiChecksGrid />
              Grid mode
            </>
          ) : (
            <>
              <LuFileJson2 />
              Import JSON
            </>
          )}
        </Button>
        <Button color="primary" onClick={handleAddColumn}>
          Add Column <RiInsertColumnRight />
        </Button>
        <Button color="primary" onClick={handleAddRow}>
          Add Row <FaArrowDownShortWide />
        </Button>
      </div>

      {inputJson ? (
        <Input
          value={jsonData}
          id="inputJSON"
          name="inputJSON"
          type="textarea"
          className={styles.jsonInputTextArea}
          onChange={handleInputJsonChange}
        />
      ) : (
        <VirtualizedPayoutTable ref={virtualizedTableRef} />
      )}
    </>
  );
});

PayoutStructureForm.displayName = 'PayoutStructureForm';

export default PayoutStructureForm;
